import { defineStore } from "pinia";
import { ref } from "vue";
import { 
  TenantService, 
  UserService, 
  type GetApiTenantExistsByCompanyResponse, 
  type GetApiUserExistsByEmailResponse, 
  type PostApiTenantActivateResponse, 
  type PutApiTenantAdminCreateResponse,
  type GetApiTenantResponse,
  type PutApiTenantResponse,
  type GetApiTenantStatsResponse,
  type PostApiTenantInvitationsResponse,
  type PostApiTenantInvitationsRevokeResponse,
  type PostApiTenantInvitationsAcceptResponse,
  type AcceptInviteRequest,
  type GetApiTenantInvitationsResponse,
  type PostApiTenantInvitationsResendResponse,
  OpenAPI,
  type GetApiTenantInvitationByIdResponse,
  type TenantDto} from "@/client/";
import {useUserStore} from "@/stores/user.store";
import { event } from 'vue-gtag'


export const useTenantStore = defineStore("tenant", () => {
  const tenants = ref<TenantDto[]>([]);
  async function registerTenant(company:string, firstName: string, lastName: string, email:string, phone: string, password: string) : Promise<PutApiTenantResponse> {
    try {
      return await TenantService.putApiTenant({requestBody: {company: company, firstName: firstName, lastName: lastName, email: email, phone: phone, password: password, referralCode: (window as any).Rewardful?.referral || ""}})
        .then((response: PutApiTenantResponse) => {
          event('register')
          const userStore = useUserStore();
          return userStore.login(email, password).then(() => {;
            return Promise.resolve(response);
          });
        })
        .catch((error:any) => {
          console.error(error);
          return Promise.reject(error);
        });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function activateTenant(id:string) {
    return await TenantService.postApiTenantActivate({requestBody: {id: id}})
      .then((response: PostApiTenantActivateResponse) => {
        return Promise.resolve(response);
      })
      .catch((error:any) => {
        return Promise.reject(error);
      });
  }

  async function checkTenantExists(name:string) {
    return await TenantService.getApiTenantExistsByCompany({company: name})
      .then((response: GetApiTenantExistsByCompanyResponse) => {
        return Promise.resolve(response);
      })
      .catch((error:any) => {
        return Promise.reject(error);
      });
  }

  async function checkEmailExists(email:string) {
    return await UserService.getApiUserExistsByEmail({email: email})
      .then((response: GetApiUserExistsByEmailResponse) => {
        return Promise.resolve(response);
      })
      .catch((error:any) => {
        return Promise.reject(error);
      });
  }

  async function getAll() {
    return await TenantService.getApiTenant()
      .then((response: GetApiTenantResponse) => {
        tenants.value = response;
        return Promise.resolve(response);
      })
      .catch((error: any) => {
        return Promise.reject(error);
      });
  }

  async function quickCreateNew(company:string, firstName:string, lastName:string, email:string, password: string) {
    return await TenantService.putApiTenantAdminCreate({requestBody: {company: company, firstName: firstName, lastName: lastName, email: email, password}})
      .then((response: PutApiTenantAdminCreateResponse) => {
        return Promise.resolve(response);
      })
      .catch((error: any) => {
        return Promise.reject(error);
      });
  }

  async function getTenantStats() {
    return await TenantService.getApiTenantStats()
      .then((response: GetApiTenantStatsResponse) => {
        return Promise.resolve(response);
      });
  }

  async function inviteUser(email: string, role: string, locationIds?: string[]) {
    return await TenantService.postApiTenantInvitations({requestBody: {email: email, role: role, locationIds: locationIds }})
      .then((response: PostApiTenantInvitationsResponse) => {
        return Promise.resolve(response);
      });
  }

  async function revokeUser(id: string) {
    return await TenantService.postApiTenantInvitationsRevoke({requestBody: {invitationId: id }})
      .then((response: PostApiTenantInvitationsRevokeResponse) => {
        return Promise.resolve(response);
      });
  }

  async function acceptInvite(request: AcceptInviteRequest) {
    return await TenantService.postApiTenantInvitationsAccept({requestBody: request})
      .then((response: PostApiTenantInvitationsAcceptResponse) => {
        return Promise.resolve(response);
      });
  }

  async function getInvitationStatus(invitationId: string) {
    return await TenantService.getApiTenantInvitationById({id: invitationId})
      .then((response: GetApiTenantInvitationByIdResponse) => {
        return Promise.resolve(response);
      })
      .catch((error: any) => {
        return Promise.reject(error);
      });
  }

  async function getPendingInvitations() {
    return await TenantService.getApiTenantInvitations()
      .then((response: GetApiTenantInvitationsResponse) => {
        return Promise.resolve(response);
      });
  }

  async function resendInvitations(id: string) {
    return await TenantService.postApiTenantInvitationsResend({requestBody: {id: id}})
      .then((response: PostApiTenantInvitationsResendResponse) => {
        return Promise.resolve(response);
      });
  }

  return {
    registerTenant,
    activateTenant,
    checkTenantExists,
    checkEmailExists,
    getAll,
    quickCreateNew,
    getTenantStats,
    inviteUser,
    revokeUser,
    acceptInvite,
    getInvitationStatus,
    getPendingInvitations,
    resendInvitations,

    tenants
  };
});

